import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useState } from 'react';
import axios from 'axios';
import {
  FileText,
  CheckCircle,
  Clock,
  Shield,
  Users,
  Award,
  Phone,
  ArrowRight,
  FileCheck,
  Building2,
  CreditCard,
  AlertTriangle,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GstRegistrationPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service_type: 'GST Registration',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contact`, {
        ...formData,
        message: `Callback request for ${formData.service_type}`,
        source_page: 'GST Registration'
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const processSteps = [
    {
      step: '01',
      title: 'Share Documents',
      description: 'Send us your required documents via email or WhatsApp for verification.',
      icon: FileText,
    },
    {
      step: '02',
      title: 'Application Filing',
      description: 'Our experts file your GST application on the official portal.',
      icon: FileCheck,
    },
    {
      step: '03',
      title: 'Get GST Number',
      description: 'Receive your GSTIN within 2-6 working days after verification.',
      icon: CheckCircle,
    },
  ];

  const documentsRequired = [
    'PAN Card of the Applicant',
    'Aadhaar Card',
    'Proof of Business Registration / Incorporation Certificate',
    'Identity & Address Proof of Promoters with Photos',
    'Address Proof of Business Premises',
    'Bank Account Statement / Cancelled Cheque',
    'Digital Signature Certificate (for companies)',
    'Authorization Letter / Board Resolution',
  ];

  const whoShouldRegister = [
    'Businesses with turnover exceeding ₹40 lakhs (₹10 lakhs for NE states)',
    'Persons making inter-state taxable supplies',
    'Casual taxable persons',
    'Persons liable to pay tax under reverse charge mechanism',
    'Non-resident taxable persons',
    'E-commerce operators',
    'Input Service Distributors',
    'Agents of suppliers',
    'Persons supplying online information and database services from abroad',
  ];

  const services = [
    {
      icon: FileText,
      title: 'GST Consultation',
      description: 'Expert guidance on GST compliance and requirements for your business.',
    },
    {
      icon: Clock,
      title: 'Monthly GST Filing',
      description: 'Timely filing of GSTR-1, GSTR-3B, and other required returns.',
    },
    {
      icon: Shield,
      title: 'GST Updates & Advisory',
      description: 'Stay updated with latest GST amendments and their impact on your business.',
    },
    {
      icon: FileCheck,
      title: 'GST Audit Support',
      description: 'Complete support during GST audits and assessments.',
    },
  ];

  const stats = [
    { value: '8000+', label: 'Happy Customers', icon: Users },
    { value: '5000+', label: 'GST Registrations', icon: FileText },
    { value: '20+', label: 'Expert Team', icon: Award },
    { value: '4.9', label: 'Google Rating', icon: Star },
  ];

  const faqs = [
    {
      question: 'Is GST registration mandatory?',
      answer: 'Yes, GST registration is mandatory once your annual turnover exceeds ₹40 lakhs (₹10 lakhs for special category states). Certain businesses like e-commerce operators and inter-state suppliers must register regardless of turnover.',
    },
    {
      question: 'How long does GST registration take?',
      answer: 'GST registration typically takes 2-6 working days after submission of all required documents. The timeline may vary based on verification requirements and department workload.',
    },
    {
      question: 'Can I apply for multiple GST registrations?',
      answer: 'Yes, if your business operates in multiple states, you need separate GST registration for each state. Each state will have a unique GSTIN.',
    },
    {
      question: 'What is the Composition Scheme under GST?',
      answer: 'The Composition Scheme is for small taxpayers with turnover up to ₹1.5 crore (₹75 lakhs for special states). It offers lower tax rates and simpler compliance requirements. Service providers with turnover up to ₹50 lakhs can also opt for this scheme.',
    },
    {
      question: 'What happens if I don\'t register for GST?',
      answer: 'Operating without GST registration when required is an offense. Penalties include 10% of tax due (minimum ₹10,000) for genuine errors, and 100% of tax due for deliberate evasion. Additionally, you cannot collect GST or claim input tax credit.',
    },
  ];

  return (
    <div data-testid="gst-registration-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section 
        data-testid="gst-hero"
        className="relative py-16 md:py-24 overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="text-slate-400 text-sm">4.9 (668+ ratings)</span>
              </div>

              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white leading-tight">
                GST Registration{' '}
                <span className="text-gradient">Services</span>
              </h1>

              <p className="text-lg text-slate-400 leading-relaxed">
                Make your growing business GST Compliant. Apply for GST now and get expert 
                help on GST Registration and Filing. Fast & easy process with our dedicated team.
              </p>

              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-blue-400" />
                  <span>Quick & Easy Registration</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-blue-400" />
                  <span>Expert Tax Advice</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-blue-400" />
                  <span>2-6 Working Days</span>
                </div>
              </div>

              <a href="tel:+919717705640">
                <Button 
                  data-testid="hero-call-button"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Call: +91-9717705640
                </Button>
              </a>
            </div>

            {/* Right Form */}
            <div 
              id="contact-form"
              className="bg-navy-800 border border-white/10 rounded-2xl p-8"
            >
              {isSubmitted ? (
                <div data-testid="callback-success" className="text-center py-8">
                  <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-white mb-2">
                    Request Received!
                  </h3>
                  <p className="text-slate-400">
                    Our team will call you back shortly.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-heading font-bold text-xl text-white mb-6">
                    Request a Call Back for GST Registration
                  </h3>
                  <form onSubmit={handleSubmit} data-testid="callback-form" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-300">Your Name</Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="callback-name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter your name"
                        className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-300">Contact Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="callback-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+91 XXXXX XXXXX"
                        className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-300">Email Address</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="callback-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service" className="text-slate-300">Select Service</Label>
                      <Select 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, service_type: value }))}
                        defaultValue="GST Registration"
                      >
                        <SelectTrigger 
                          data-testid="callback-service"
                          className="bg-navy-900 border-white/10 text-white"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-navy-800 border-white/10">
                          <SelectItem value="GST Registration" className="text-white hover:bg-navy-700">GST Registration</SelectItem>
                          <SelectItem value="GST Filing" className="text-white hover:bg-navy-700">GST Filing</SelectItem>
                          <SelectItem value="ITR Filing" className="text-white hover:bg-navy-700">ITR Filing</SelectItem>
                          <SelectItem value="Company Registration" className="text-white hover:bg-navy-700">Company Registration</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="submit"
                      data-testid="callback-submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white py-6"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* What is GST Section */}
      <section 
        data-testid="what-is-gst"
        className="py-16 md:py-24 bg-navy-800/50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-blue-400 font-medium mb-4 block">Understanding GST</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
                What is GST Registration?
              </h2>
              <div className="space-y-4 text-slate-400 leading-relaxed">
                <p>
                  GST (Goods and Services Tax) Registration is mandatory for businesses whose 
                  annual turnover exceeds ₹40 lakhs (₹10 lakhs for North-Eastern and hill states). 
                  This registration process provides a unique GSTIN (GST Identification Number) 
                  to your business.
                </p>
                <p>
                  Operating without GST registration when required is an offense under the GST Act, 
                  attracting heavy penalties. Our expert team helps you complete the registration 
                  in just 2-6 working days through a simple 3-step process.
                </p>
              </div>
              <a href="tel:+919717705640" className="inline-block mt-6">
                <Button 
                  data-testid="call-now-button"
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Phone className="mr-2 w-4 h-4" />
                  Call: +91-9717705640
                </Button>
              </a>
            </div>
            <div className="bg-navy-800 border border-white/5 rounded-2xl p-8">
              <h3 className="font-heading font-semibold text-xl text-white mb-6">
                Key Benefits of GST Registration
              </h3>
              <ul className="space-y-4">
                {[
                  'Legal recognition as a supplier of goods/services',
                  'Ability to collect GST from customers',
                  'Claim Input Tax Credit on purchases',
                  'Interstate business operations',
                  'Access to government tenders and contracts',
                  'Build trust with customers and vendors',
                ].map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section 
        data-testid="process-section"
        className="py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-blue-400 font-medium mb-4 block">Our Process</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              How We Work
            </h2>
            <p className="text-slate-400">
              Simple 3-step process to get your GST registration done quickly.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div 
                key={index}
                data-testid={`process-step-${index}`}
                className="relative"
              >
                <div className="bg-navy-800 border border-white/5 rounded-2xl p-8 card-hover h-full">
                  <div className="w-14 h-14 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-6">
                    <step.icon className="w-7 h-7 text-blue-400" />
                  </div>
                  <div className="text-blue-400 font-heading font-bold text-sm mb-2">
                    Step {step.step}
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-slate-400">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-blue-500/30" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Should Register */}
      <section 
        data-testid="who-should-register"
        className="py-16 md:py-24 bg-navy-800/50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <span className="text-blue-400 font-medium mb-4 block">Eligibility</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
                Who Should Register for GST?
              </h2>
              <p className="text-slate-400 mb-8">
                As per Section 24 of the CGST Act, 2017, the following categories of persons 
                are mandatorily required to register under GST:
              </p>
              <ul className="space-y-3">
                {whoShouldRegister.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Documents Required */}
            <div>
              <span className="text-blue-400 font-medium mb-4 block">Documentation</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
                Documents Required
              </h2>
              <div className="bg-navy-800 border border-white/5 rounded-2xl p-8">
                <ul className="space-y-4">
                  {documentsRequired.map((doc, index) => (
                    <li 
                      key={index}
                      data-testid={`document-${index}`}
                      className="flex items-start gap-3"
                    >
                      <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-blue-400" />
                      </div>
                      <span className="text-slate-300">{doc}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-white/10 flex gap-4">
                  <a href="tel:+919717705640">
                    <Button 
                      data-testid="docs-call-button"
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                    >
                      <Phone className="mr-2 w-4 h-4" />
                      Call Now
                    </Button>
                  </a>
                  <Link to="/contact">
                    <Button 
                      data-testid="docs-contact-button"
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      Get Started
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section 
        data-testid="stats-section"
        className="py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-blue-400 font-medium mb-4 block">Why Choose Us</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Why Choose TaxCompany
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div 
                key={index}
                data-testid={`stat-${index}`}
                className="bg-navy-800 border border-white/5 rounded-2xl p-6 text-center card-hover"
              >
                <div className="w-12 h-12 mx-auto rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-4">
                  <stat.icon className="w-6 h-6 text-blue-400" />
                </div>
                <p className="font-heading font-bold text-3xl text-blue-400 mb-1">{stat.value}</p>
                <p className="text-slate-400 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services We Offer */}
      <section 
        data-testid="services-section"
        className="py-16 md:py-24 bg-navy-800/50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-blue-400 font-medium mb-4 block">Our Services</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              What We Offer in GST Registration
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-card-${index}`}
                className="bg-navy-800 border border-white/5 rounded-2xl p-6 card-hover group"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                  <service.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2">
                  {service.title}
                </h3>
                <p className="text-slate-400 text-sm">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Penalty Section */}
      <section 
        data-testid="penalty-section"
        className="py-16 md:py-24"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-2xl p-8 md:p-12">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-7 h-7 text-red-400" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-white mb-4">
                  Penalty for Not Registering Under GST
                </h2>
                <div className="space-y-3 text-slate-300">
                  <p>
                    Operating without GST registration when required can result in severe penalties:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">•</span>
                      <span>10% of tax due (minimum ₹10,000) for genuine errors</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">•</span>
                      <span>100% of tax due for deliberate evasion</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">•</span>
                      <span>Cascading effect on subsequent return filings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">•</span>
                      <span>Inability to claim Input Tax Credit</span>
                    </li>
                  </ul>
                </div>
                <a href="#contact-form" className="inline-block mt-6">
                  <Button 
                    data-testid="avoid-penalty-button"
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    Get Registered Now
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section 
        data-testid="faq-section"
        className="py-16 md:py-24 bg-navy-800/50"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-blue-400 font-medium mb-4 block">FAQs</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                data-testid={`faq-${index}`}
                className="bg-navy-800 border border-white/5 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                >
                  <span className="font-heading font-semibold text-white pr-4">
                    {faq.question}
                  </span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <p className="text-slate-400 mb-4">Still have questions?</p>
            <a href="tel:+919717705640">
              <Button 
                data-testid="faq-call-button"
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Phone className="mr-2 w-4 h-4" />
                Call: +91-9717705640
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section 
        data-testid="final-cta"
        className="py-16 md:py-24"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="bg-gradient-to-r from-blue-500/10 to-sky-500/10 border border-white/10 rounded-3xl p-10 md:p-14 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
                Ready to Get GST Registered?
              </h2>
              <p className="text-slate-400 mb-8 max-w-xl mx-auto">
                Our expert team is ready to help you with quick and hassle-free GST registration. 
                Get started today!
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="#contact-form">
                  <Button 
                    data-testid="final-cta-form"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg"
                  >
                    Request Callback
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <Link to="/contact">
                  <Button 
                    data-testid="final-cta-contact"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg"
                  >
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
